import { useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import type {
  TenantSalesChannel,
  TenantSalesChannelKind,
} from '../../api/salesChannelsApi';
import {
  useCreateSalesChannelRegistry,
  useDeactivateSalesChannelRegistry,
  useSalesChannelsRegistry,
  useUpdateSalesChannelRegistry,
  useUpdateTouchpointRegistry,
} from '../../hooks';
import { useFeedback } from '../../lib';
import { STORE_SETTINGS_PERMISSIONS } from '../../model/permissions';
import { FeedbackSnackbar } from '../components/FeedbackSnackbar';
import { FieldRow } from '../components/FieldRow';
import { SettingsCard } from '../components/SettingsCard';

/** Known marketplaces offered by the create dialog (code → i18n label key suffix). */
const KNOWN_MARKETPLACES = [
  { code: 'MP_WILDBERRIES', label: 'Wildberries' },
  { code: 'MP_OZON', label: 'Ozon' },
  { code: 'MP_YANDEX_MARKET', label: 'Yandex Market' },
  { code: 'MP_LAMODA', label: 'Lamoda' },
] as const;

const KIND_CHIP_COLOR: Record<TenantSalesChannelKind, 'primary' | 'secondary' | 'default'> = {
  OWN_ONLINE: 'primary',
  OWN_RETAIL: 'secondary',
  MARKETPLACE: 'default',
  B2B_WHOLESALE: 'default',
};

/**
 * "Sales channels" — the tenant's unified sales-channel & touchpoint registry
 * (adr-tenant-001, plan-tenant-001 Phase 5 → web-tenant-settings-service via the BFF).
 *
 * Channel *identity* is managed here and ONLY here; per-channel behaviour stays in its own
 * section (prices -> Price settings / catalog bindings -> Catalog publication) and references the
 * canonical channel codes shown on this screen. Touchpoint switches are the per-touchpoint
 * kill-switches (registry-side SoR of the legacy CRM channel flags).
 */
export function SalesChannelsPage() {
  const { t } = useTranslation('settings');
  const { hasPermission } = usePermissions();
  const canView = hasPermission(STORE_SETTINGS_PERMISSIONS.channels.view);
  const canManage = hasPermission(STORE_SETTINGS_PERMISSIONS.channels.manage);

  const { data: channels, isLoading, isError, refetch } = useSalesChannelsRegistry();
  const createChannel = useCreateSalesChannelRegistry();
  const updateChannel = useUpdateSalesChannelRegistry();
  const deactivateChannel = useDeactivateSalesChannelRegistry();
  const updateTouchpoint = useUpdateTouchpointRegistry();
  const { feedback, success, error: showError, clear } = useFeedback();

  const [createOpen, setCreateOpen] = useState(false);
  const [marketplace, setMarketplace] = useState<string>(KNOWN_MARKETPLACES[0].code);
  const [customCode, setCustomCode] = useState('');
  const [customName, setCustomName] = useState('');
  const [deactivating, setDeactivating] = useState<TenantSalesChannel | null>(null);

  const sortedChannels = useMemo(
    () => (channels ? [...channels].sort((a, b) => a.sortOrder - b.sortOrder) : []),
    [channels],
  );

  if (!canView) {
    return (
      <Box sx={{ py: 6 }}>
        <ErrorState message={t('salesChannelsPage.noAccess')} />
      </Box>
    );
  }
  if (isLoading) return <LoadingState label={t('salesChannelsPage.loading')} />;
  if (isError) {
    return (
      <ErrorState message={t('salesChannelsPage.loadError')} onRetry={() => void refetch()} />
    );
  }

  const isCustom = marketplace === 'CUSTOM';
  const submitCreate = () => {
    const known = KNOWN_MARKETPLACES.find((m) => m.code === marketplace);
    const code = isCustom ? customCode.trim().toUpperCase() : marketplace;
    const name = isCustom ? customName.trim() || code : (known?.label ?? code);
    createChannel.mutate(
      { code, kind: 'MARKETPLACE', name },
      {
        onSuccess: () => {
          setCreateOpen(false);
          setCustomCode('');
          setCustomName('');
          success(t('salesChannelsPage.createdFeedback'));
        },
        onError: (e) => showError(e.message),
      },
    );
  };

  return (
    <Box
      sx={{ width: '100%', mx: 'auto', maxWidth: 1100, px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            aria-hidden
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 44,
              height: 44,
              borderRadius: 2.5,
              color: 'primary.contrastText',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            }}
          >
            <HubRoundedIcon />
          </Box>
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 800 }}>
              {t('salesChannelsPage.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('salesChannelsPage.subtitle')}
            </Typography>
          </Box>
        </Stack>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => setCreateOpen(true)}
          >
            {t('salesChannelsPage.addMarketplace')}
          </Button>
        )}
      </Stack>

      {sortedChannels.length === 0 ? (
        <EmptyState
          title={t('salesChannelsPage.emptyTitle')}
          description={t('salesChannelsPage.emptyDescription')}
        />
      ) : (
        <Stack spacing={2.5}>
          {sortedChannels.map((channel) => (
            <SettingsCard
              key={channel.code}
              title={
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <span>{channel.name}</span>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      channel.kind
                        ? t(`salesChannelsPage.kind.${channel.kind}`)
                        : t('salesChannelsPage.kind.UNKNOWN')
                    }
                    color={channel.kind ? KIND_CHIP_COLOR[channel.kind] : 'default'}
                  />
                  {channel.defaultChannel && (
                    <Chip size="small" color="primary" label={t('salesChannelsPage.defaultChip')} />
                  )}
                  {!channel.active && (
                    <Chip size="small" label={t('salesChannelsPage.inactiveChip')} />
                  )}
                </Stack>
              }
              description={
                <Typography variant="caption" sx={{ fontFamily: '"JetBrains Mono", monospace' }}>
                  {channel.code}
                </Typography>
              }
              action={
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    component={RouterLink}
                    to="/catalog/pricing"
                    endIcon={<LaunchRoundedIcon sx={{ fontSize: 14 }} />}
                  >
                    {t('salesChannelsPage.pricesLink')}
                  </Button>
                  <Button
                    size="small"
                    component={RouterLink}
                    to="/catalog/publication"
                    endIcon={<LaunchRoundedIcon sx={{ fontSize: 14 }} />}
                  >
                    {t('salesChannelsPage.catalogsLink')}
                  </Button>
                  {canManage && !channel.defaultChannel && channel.active && (
                    <Button size="small" color="error" onClick={() => setDeactivating(channel)}>
                      {t('salesChannelsPage.deactivate')}
                    </Button>
                  )}
                </Stack>
              }
            >
              <FieldRow
                label={t('salesChannelsPage.activeLabel')}
                description={t('salesChannelsPage.activeDescription')}
              >
                <Tooltip
                  title={channel.defaultChannel ? t('salesChannelsPage.defaultCannotDeactivate') : ''}
                >
                  <span>
                    <Switch
                      checked={channel.active}
                      disabled={!canManage || channel.defaultChannel}
                      onChange={(_, checked) =>
                        updateChannel.mutate(
                          { code: channel.code, body: { active: checked } },
                          {
                            onSuccess: () => success(t('salesChannelsPage.savedFeedback')),
                            onError: (e) => showError(e.message),
                          },
                        )
                      }
                      slotProps={{
                        input: {
                          'aria-label': t('salesChannelsPage.activeAria', {
                            channel: channel.name,
                          }),
                        },
                      }}
                    />
                  </span>
                </Tooltip>
              </FieldRow>
              {channel.touchpoints.length > 0 && (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    {t('salesChannelsPage.touchpointsHeading')}
                  </Typography>
                  {channel.touchpoints.map((touchpoint) => (
                    <FieldRow
                      key={touchpoint.code}
                      label={touchpoint.name}
                      description={
                        touchpoint.type
                          ? t(`salesChannelsPage.touchpointType.${touchpoint.type}`)
                          : touchpoint.code
                      }
                    >
                      <Switch
                        checked={touchpoint.enabled}
                        disabled={!canManage}
                        onChange={(_, checked) =>
                          updateTouchpoint.mutate(
                            { code: touchpoint.code, body: { enabled: checked } },
                            {
                              onSuccess: () => success(t('salesChannelsPage.savedFeedback')),
                              onError: (e) => showError(e.message),
                            },
                          )
                        }
                        slotProps={{
                          input: {
                            'aria-label': t('salesChannelsPage.touchpointAria', {
                              touchpoint: touchpoint.name,
                            }),
                          },
                        }}
                      />
                    </FieldRow>
                  ))}
                </Box>
              )}
            </SettingsCard>
          ))}
        </Stack>
      )}

      {/* Create-marketplace dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('salesChannelsPage.createDialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label={t('salesChannelsPage.createDialog.marketplaceLabel')}
              value={marketplace}
              onChange={(e) => setMarketplace(e.target.value)}
            >
              {KNOWN_MARKETPLACES.map((m) => (
                <MenuItem key={m.code} value={m.code}>
                  {m.label}
                </MenuItem>
              ))}
              <MenuItem value="CUSTOM">{t('salesChannelsPage.createDialog.customOption')}</MenuItem>
            </TextField>
            {isCustom && (
              <>
                <TextField
                  fullWidth
                  label={t('salesChannelsPage.createDialog.codeLabel')}
                  helperText={t('salesChannelsPage.createDialog.codeHelper')}
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value)}
                />
                <TextField
                  fullWidth
                  label={t('salesChannelsPage.createDialog.nameLabel')}
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('salesChannelsPage.createDialog.cancel')}</Button>
          <Button
            variant="contained"
            disabled={createChannel.isPending || (isCustom && !customCode.trim())}
            onClick={submitCreate}
          >
            {t('salesChannelsPage.createDialog.submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirmation */}
      <Dialog open={deactivating !== null} onClose={() => setDeactivating(null)} maxWidth="xs">
        <DialogTitle>{t('salesChannelsPage.deactivateDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('salesChannelsPage.deactivateDialog.body', { channel: deactivating?.name ?? '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivating(null)}>
            {t('salesChannelsPage.deactivateDialog.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deactivateChannel.isPending}
            onClick={() => {
              if (!deactivating) return;
              deactivateChannel.mutate(deactivating.code, {
                onSuccess: () => {
                  setDeactivating(null);
                  success(t('salesChannelsPage.deactivatedFeedback'));
                },
                onError: (e) => showError(e.message),
              });
            }}
          >
            {t('salesChannelsPage.deactivateDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <FeedbackSnackbar feedback={feedback} onClose={clear} />
    </Box>
  );
}
